import { apiClient } from './apiClient';
import {
  OrderSide,
  MonitorStatus,
  PipelineStatus,
  BarInterval,
  RegenerateStatus,
  ComparisonOperator,
  PipelineStatusData,
} from '@/services/enums';
import { MonitorSignalCondition, MonitorSignalRule } from './klineTypes';

export interface StockMonitorRule {
  met?: boolean;
  currentValue?: number | boolean;
  ema20?: number;
}

export interface MonitorSignal extends Omit<MonitorSignalRule, 'conditions'> {
  type: OrderSide;
  conditions: Array<MonitorSignalCondition>;
  triggered: boolean;
  triggeredAt?: string;
}

export interface StockMonitorIndicators {
  // 核心价格数据
  price?: number;
  close?: number;
  high?: number;
  low?: number;
  open?: number;
  volume?: number;

  // 基础财务/市场数据 (用于列表展示)
  change?: number; // 涨跌幅
  amount?: number; // 成交额
  turnover?: number; // 换手率
  mktcap?: number; // 流通市值
  totalMktCap?: number; // 总市值
  peRatio?: number; // 市盈率
  pbRatio?: number; // 市净率
  volumeRatio?: number; // 量比
  industry?: string; // 所属行业

  // 常用指标
  rsi14?: number;
  obv_slope?: number;
  macd?: number;
  macdSignal?: number;
  macdHistogram?: number;

  // 内部计算字段（用于金叉/死叉检测）
  macdForCross?: number;
  macdSignalForCross?: number;
  macdCrossUp?: boolean;
  macdCrossDown?: boolean;

  // 趋势与背离
  macdDivergence?: number;
  macdBottomDivergence?: number;

  // 成交量相关
  volumeDecline?: number;
  volumeSurgeBullishCandle?: number;

  // 价格特征
  priceNewHigh?: number;

  // 均线相关
  ema20?: number;
  ema50?: number;
  sma50?: number;
  sma200?: number; // A股看重年线
  // 均线交叉计算字段
  ema20ForCross?: number;
  ema50ForCross?: number;
  ema20CrossUp?: boolean;
  ema20CrossDown?: boolean;

  // SMA200 (年线) 交叉
  sma200CrossUp?: boolean;
  sma200CrossDown?: boolean;

  // 布林带
  bbUpper?: number;
  bbLower?: number;
  bbMiddle?: number;

  // 其他指标
  atr?: number;
  vwap?: number;
}

export interface StockMonitorTask {
  monitorId: string;
  symbol: string;
  name?: string;
  strategyId?: string;
  bar: BarInterval;
  status: MonitorStatus | PipelineStatus;
  price?: number;
  change?: number;
  statusReason?: string;
  enableAutoTrading?: boolean;
  createdAt: string;
  updatedAt?: string;
  ruleStartTime?: number;
  ruleEndTime?: number;
  analysisScore?: number;
  analysisDescription?: string;
  systemPromptText?: string;
  userPromptText?: string;
  promptHash?: string;
  analysisReportHash?: string;
  systemPromptId?: string;
  regenerateStatus?: RegenerateStatus;
  regenerateFailureReason?: string;
  tradingParams?: any;
  backtestOptimization?: {
    paramRanges?: any;
    bestParams?: any;
    results: {
      totalReturn: string | number;
      sharpeRatio: number;
      maxDrawdown: string | number;
      winRate: number;
      trades: number;
      profitFactor: number;
      annualReturn?: string | number;
    };
    lastRunTime?: string;
    bestScore?: number;
    status?: string;
    endTime?: string;
  };
  signals: Array<MonitorSignal>;
  currentData?: StockMonitorIndicators;
}

export interface StockBasicInfo {
  symbol: string;
  name: string;
  area?: string;
  industry?: string;
}

export class StockMonitorService {
  /**
   * 搜索股票
   */
  async searchStocks(keyword?: string, limit?: number): Promise<StockBasicInfo[]> {
    try {
      const response = await apiClient.get<StockBasicInfo[]>('/stock/list', {
        params: { keyword, limit },
      });

      if (!response.success) {
        throw new Error(response.message || '获取股票列表失败');
      }

      return response.data || [];
    } catch (error: any) {
      console.error('Search stocks error:', error);
      throw error;
    }
  }

  /**
   * 获取所有监控任务（支持分页、status 和 symbol 过滤）
   */
  async getAllRunningMonitors(
    status?: MonitorStatus,
    symbol?: string,
    page?: number,
    pageSize?: number,
  ): Promise<{ monitors: StockMonitorTask[]; total: number; runningCount: number }> {
    const params: any = {};
    if (status) {
      params.status = status;
    }
    if (symbol) {
      params.symbol = symbol;
    }
    if (page) {
      params.page = page;
    }
    if (pageSize) {
      params.pageSize = pageSize;
    }

    const response = await apiClient.get<{
      monitors: StockMonitorTask[];
      total: number;
      runningCount: number;
      page: number;
      pageSize: number;
    }>('/stock/monitor/list', { params });

    if (!response.success) {
      throw new Error(response.message || '获取监控任务列表失败');
    }

    // 适配新的分页数据结构
    const data = response.data;
    if (data && Array.isArray(data.monitors)) {
      return {
        monitors: data.monitors,
        total: data.total || 0,
        runningCount: data.runningCount || 0,
      };
    }

    // 兜底处理旧格式（如果后端还没更新或返回的是数组）
    const arrayData = Array.isArray(response.data) ? response.data : [];
    return {
      monitors: arrayData,
      total: (response as any).total || arrayData.length,
      runningCount: arrayData.filter((m: any) => m.status === 'running').length,
    };
  }

  /**
   * 获取监控任务状态
   */
  async getMonitorStatus(monitorId: string): Promise<StockMonitorTask> {
    const response = await apiClient.get<StockMonitorTask>('/stock/monitor/status', {
      params: { monitorId },
    });

    if (!response.success) {
      throw new Error(response.message || '获取监控任务状态失败');
    }

    return response.data;
  }

  /**
   * 获取所有监控任务 (包装 getAllRunningMonitors)
   */
  async getAllMonitors(symbol?: string): Promise<{ monitors: StockMonitorTask[]; total: number }> {
    return this.getAllRunningMonitors(undefined, symbol);
  }

  /**
   * 获取每日潜力股
   */
  async getDailyPicks(date?: string): Promise<StockMonitorTask[]> {
    try {
      const response = await apiClient.get<StockMonitorTask[]>('/stock/monitor/daily-picks', {
        params: { date },
      });

      if (!response.success) {
        throw new Error(response.message || '获取每日潜力股失败');
      }

      return response.data || [];
    } catch (error: any) {
      console.error('Get daily picks error:', error);
      throw error;
    }
  }

  /**
   * 获取研报管道状态
   */
  async getPipelineStatus(): Promise<boolean> {
    try {
      const response = await apiClient.get<{ enabled: boolean }>('/stock/monitor/pipeline/status');

      if (!response.success) {
        throw new Error(response.message || '获取研报管道状态失败');
      }

      return response.data?.enabled || false;
    } catch (error: any) {
      console.error('Get pipeline status error:', error);
      throw error;
    }
  }

  /**
   * 切换研报管道状态
   */
  async togglePipelineStatus(enabled: boolean): Promise<boolean> {
    try {
      const response = await apiClient.post<{ enabled: boolean }>('/stock/monitor/pipeline/toggle', {
        enabled,
      });

      if (!response.success) {
        throw new Error(response.message || '切换研报管道状态失败');
      }

      return response.data?.enabled || false;
    } catch (error: any) {
      console.error('Toggle pipeline status error:', error);
      throw error;
    }
  }

  /**
   * 启动监控
   */
  async startMonitor(
    symbol: string,
    bar: BarInterval = '1D', // A股默认日线
    enableAutoTrading: boolean = false,
    fixedRuleSignals: Array<{
      type: OrderSide;
      description: string;
      conditions: Array<{
        field: string;
        operator: ComparisonOperator;
        value: number;
      }>;
      triggered: boolean;
    }> = [],
    systemPromptId?: string,
  ): Promise<{
    monitorId: string;
    signals: Array<any>;
  }> {
    try {
      const response = await apiClient.post<{
        monitorId: string;
        signals: Array<any>;
      }>('/stock/monitor/start', {
        symbol,
        bar,
        enableAutoTrading,
        fixedRuleSignals,
        systemPromptId,
      });

      if (!response.success) {
        throw new Error(response.message || '启动监控失败');
      }

      return response.data;
    } catch (error: any) {
      console.error('Start monitor error:', error);
      throw error;
    }
  }

  /**
   * 停止监控
   */
  async stopMonitor(monitorId: string): Promise<void> {
    try {
      const response = await apiClient.post('/stock/monitor/stop', {
        monitorId,
      });

      if (!response.success) {
        throw new Error(response.message || '停止监控失败');
      }
    } catch (error: any) {
      console.error('Stop monitor error:', error);
      throw error;
    }
  }

  /**
   * 重新启动监控 (实际上是先停止再启动，或者直接启动因为后端做了幂等)
   * 为了简化，这里直接调用 startMonitor
   */
  async restartMonitor(symbol: string, bar: BarInterval, enableAutoTrading: boolean = false): Promise<void> {
    await this.startMonitor(symbol, bar, enableAutoTrading);
  }

  /**
   * 删除监控任务
   */
  async deleteMonitor(monitorId: string): Promise<void> {
    try {
      const response = await apiClient.delete('/stock/monitor', {
        params: { monitorId },
      });

      if (!response.success) {
        throw new Error(response.message || '删除监控任务失败');
      }
    } catch (error: any) {
      console.error('Delete monitor error:', error);
      throw error;
    }
  }

  /**
   * 批量删除停止运行的监控任务
   */
  async deleteStoppedMonitors(): Promise<number> {
    try {
      const response = await apiClient.delete<{ deletedCount: number }>('/stock/monitor/stopped');

      if (!response.success) {
        throw new Error(response.message || '批量删除监控任务失败');
      }

      return response.data.deletedCount;
    } catch (error: any) {
      console.error('Delete stopped monitors error:', error);
      throw error;
    }
  }

  /**
   * 获取研报管理服务状态
   */
  async getResearchStatus(): Promise<{
    pipelineStatus?: PipelineStatusData | null;
  }> {
    const response = await apiClient.get<any>('/stock/research/status');

    if (!response.success) {
      throw new Error(response.message || '获取研报状态失败');
    }

    return {
      pipelineStatus: response.data?.pipelineStatus || null,
    };
  }

  /**
   * 获取所有策略
   */
  async getAllStrategies(): Promise<
    Array<{
      strategyId: string;
      name: string;
      description: string;
      isSystem: boolean;
    }>
  > {
    const response = await apiClient.get<any[]>('/stock-strategy/strategies');

    if (!response.success) {
      throw new Error(response.message || '获取策略列表失败');
    }

    return response.data || [];
  }

  /**
   * 获取研报管理服务配置
   */
  async getResearchConfig(): Promise<{
    monitorCount: number;
    strategyIds: string[];
    excludedSymbols: string[];
    minAmount?: number;
    filterAnalyzed?: boolean;
    symbolKeyword?: string;
  }> {
    const response = await apiClient.get<any>('/stock/research/config');

    if (!response.success) {
      throw new Error(response.message || '获取A股研报配置失败');
    }

    return response.data;
  }

  /**
   * 同步研报任务
   */
  async syncResearchTasks(
    monitorCount?: number,
    excludedSymbols?: string[],
    strategyIds: string[] = ['hot_stocks'],
    minAmount?: number,
    filterAnalyzed?: boolean,
    symbolKeyword?: string,
  ): Promise<void> {
    const response = await apiClient.post('/stock/research/sync-tasks', {
      monitorCount,
      excludedSymbols,
      strategyIds,
      minAmount,
      filterAnalyzed,
      symbolKeyword,
    });

    if (!response.success) {
      throw new Error(response.message || '启动A股研报任务同步失败');
    }
  }

  /**
   * 预览研报任务股票
   */
  async previewResearchSymbols(
    monitorCount: number,
    excludedSymbols: string[],
    strategyIds?: string[],
    minAmount?: number,
    filterAnalyzed?: boolean,
    symbolKeyword?: string,
  ): Promise<
    Array<{
      symbol: string;
      name: string;
      amount: number;
      turnoverRate: number;
      volatility: number;
    }>
  > {
    const response = await apiClient.post<any[]>('/stock/research/preview-symbols', {
      monitorCount,
      excludedSymbols,
      strategyIds,
      minAmount,
      filterAnalyzed,
      symbolKeyword,
    });

    if (!response.success) {
      throw new Error(response.message || '预览自动监控股票失败');
    }

    return response.data;
  }

  /**
   * 根据 promptHash 获取提示词内容
   * @param hash promptHash
   * @param bar 可选，时间粒度（如果提供，会同时返回 systemPrompt）
   */
  async getPrompt(
    hash: string,
    bar?: string,
  ): Promise<{
    systemPrompt?: string;
    userPrompt: string;
    hash: string;
  }> {
    const params = bar ? { bar } : {};
    const response = await apiClient.get<{
      systemPrompt?: string;
      userPrompt: string;
      hash: string;
    }>(`/stock/prompt/${hash}`, { params });

    if (!response.success) {
      throw new Error(response.message || '获取提示词失败');
    }

    return response.data;
  }
}

export const stockMonitorService = new StockMonitorService();
